import { useState } from 'react';
import { Plus, Edit, Trash2, X, ArrowLeft } from 'lucide-react';
import type { Task, Category } from '../../App';
import type { Vendor } from './VendorsScreen';
import type { MicrosoftAccount } from './PINCodesScreen';
import { TaskForm } from './TaskForm';

interface CategoriesAndTasksScreenProps {
  categories: Category[];
  tasks: Task[];
  vendors: Vendor[];
  microsoftAccounts: MicrosoftAccount[];
  onAddCategory: (category: Category) => void;
  onUpdateCategory: (category: Category) => void;
  onDeleteCategory: (categoryId: string) => void;
  onAddTask: (task: Task) => void;
  onUpdateTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
}

export function CategoriesAndTasksScreen({
  categories,
  tasks,
  vendors,
  microsoftAccounts,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
}: CategoriesAndTasksScreenProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    categories.length > 0 ? categories[0].id : ''
  );
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Category handlers
  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    const categoryId = newCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    if (categories.some(c => c.id === categoryId)) {
      alert('A category with this name already exists');
      return;
    }

    const newCategory: Category = {
      id: categoryId,
      name: newCategoryName.trim(),
    };

    onAddCategory(newCategory);
    setNewCategoryName('');
    setIsCreatingCategory(false);
    setSelectedCategoryId(categoryId);
  };

  const handleUpdateCategory = () => {
    if (!editingCategory || !newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    const updatedCategory: Category = {
      ...editingCategory,
      name: newCategoryName.trim(),
    };

    onUpdateCategory(updatedCategory);
    setEditingCategory(null);
    setNewCategoryName('');
  };

  const handleDeleteCategory = (categoryId: string) => {
    const categoryTasks = tasks.filter(t => t.categoryId === categoryId);
    
    if (categoryTasks.length > 0) {
      if (!window.confirm(`This category has ${categoryTasks.length} task(s). Deleting the category will also delete all associated tasks. Continue?`)) {
        return;
      }
      // Delete all tasks in this category
      categoryTasks.forEach(task => onDeleteTask(task.id));
    }

    onDeleteCategory(categoryId);
    
    // Select another category if the deleted one was selected
    if (selectedCategoryId === categoryId) {
      const remainingCategories = categories.filter(c => c.id !== categoryId);
      setSelectedCategoryId(remainingCategories.length > 0 ? remainingCategories[0].id : '');
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setIsCreatingCategory(false);
  };

  const cancelCategoryEdit = () => {
    setIsCreatingCategory(false);
    setEditingCategory(null);
    setNewCategoryName('');
  };

  // Task handlers
  const handleSaveTask = (task: Task) => {
    if (editingTask) {
      onUpdateTask(task);
    } else {
      onAddTask(task);
    }
    setIsCreatingTask(false);
    setEditingTask(null);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsCreatingTask(true);
  };

  const handleDeleteTask = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      onDeleteTask(taskId);
    }
  };

  const handleBackFromForm = () => {
    setIsCreatingTask(false);
    setEditingTask(null);
  };

  // Get tasks for selected category
  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const categoryTasks = tasks.filter(t => t.categoryId === selectedCategoryId);

  // If task form is open
  if (isCreatingTask || editingTask) {
    return (
      <div className="p-6">
        <button
          onClick={handleBackFromForm}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tasks
        </button>
        <TaskForm
          task={editingTask || undefined}
          categories={categories}
          vendors={vendors}
          microsoftAccounts={microsoftAccounts}
          defaultCategoryId={selectedCategoryId}
          onSave={handleSaveTask}
          onCancel={handleBackFromForm}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 pb-4 bg-white border-b border-gray-200">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Categories & Tasks</h2>
          <p className="text-gray-600 mt-1">Manage your task categories and tasks</p>
        </div>

        {/* Category Selection and Management */}
        <div className="space-y-4">
          {/* Category Dropdown and Actions */}
          <div className="flex items-center gap-3">
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white"
              >
                {categories.length === 0 && <option value="">No categories available</option>}
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedCategory && (
              <button
                onClick={() => handleEditCategory(selectedCategory)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition mt-6"
                title="Edit category"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            
            {selectedCategory && (
              <button
                onClick={() => handleDeleteCategory(selectedCategoryId)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition mt-6"
                title="Delete category"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={() => setIsCreatingCategory(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium mt-6"
            >
              <Plus className="w-4 h-4" />
              New Category
            </button>
          </div>

          {/* Category Creation/Edit Form */}
          {(isCreatingCategory || editingCategory) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h4>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      editingCategory ? handleUpdateCategory() : handleCreateCategory();
                    } else if (e.key === 'Escape') {
                      cancelCategoryEdit();
                    }
                  }}
                  placeholder="Category name"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                  autoFocus
                />
                <button
                  onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={cancelCategoryEdit}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tasks Section */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedCategory ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No categories yet. Create your first category to get started.</p>
          </div>
        ) : (
          <>
            {/* Task Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Tasks in {selectedCategory.name}
              </h3>
              <button
                onClick={() => setIsCreatingTask(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
              >
                <Plus className="w-4 h-4" />
                New Task
              </button>
            </div>

            {/* Task List */}
            {categoryTasks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500 mb-4">No tasks in this category yet.</p>
                <button
                  onClick={() => setIsCreatingTask(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create First Task
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {categoryTasks.map((task) => (
                  <div
                    key={task.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition group"
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleEditTask(task)}
                        className="flex-1 text-left"
                      >
                        <h4 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition">
                          {task.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {task.questions.length} question{task.questions.length !== 1 ? 's' : ''}
                        </p>
                      </button>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditTask(task)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit task"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete task"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}